import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CAMERA_FALLBACK_RATIO, cameraRatio, layoutCameraGrid } from "./cameraGridLayout";
const CameraGridContext = createContext(null);
const PADDING = 4;
/** Owns only camera geometry. Resize reads are batched; absolutely positioned
 * children cannot determine the observed parent's size (no feedback loop). */
export default function AdaptiveCameraGrid({ ids, children, }) {
    const ref = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [ratios, setRatios] = useState({});
    const [focused, setFocused] = useState(null);
    const [labelHeight, setLabelHeight] = useState(32);
    const pendingRatios = useRef(new Map());
    const ratioFrame = useRef(0);
    const signature = JSON.stringify(ids);
    const orderedIds = useMemo(() => JSON.parse(signature), [signature]);
    const currentIds = useRef(new Set(ids));
    currentIds.current = new Set(ids);
    const setRatio = useCallback((id, ratio) => {
        if (!currentIds.current.has(id))
            return;
        const next = cameraRatio(ratio);
        pendingRatios.current.set(id, next);
        if (ratioFrame.current)
            return;
        ratioFrame.current = window.requestAnimationFrame(() => {
            ratioFrame.current = 0;
            const pending = new Map(pendingRatios.current);
            pendingRatios.current.clear();
            setRatios(old => {
                let nextRatios = old;
                pending.forEach((value, key) => {
                    if (currentIds.current.has(key) && Math.abs((old[key] ?? CAMERA_FALLBACK_RATIO) / value - 1) >= .01) {
                        if (nextRatios === old)
                            nextRatios = { ...old };
                        nextRatios[key] = value;
                    }
                });
                return nextRatios;
            });
        });
    }, []);
    useEffect(() => () => {
        if (ratioFrame.current)
            window.cancelAnimationFrame(ratioFrame.current);
        pendingRatios.current.clear();
        ratioFrame.current = 0;
    }, []);
    const toggle = useCallback((id) => setFocused(old => old === id ? null : id), []);
    useEffect(() => {
        setFocused(old => old && currentIds.current.has(old) ? old : null);
        setRatios(old => {
            const keys = Object.keys(old);
            return keys.some(id => !currentIds.current.has(id))
                ? Object.fromEntries(keys.filter(id => currentIds.current.has(id)).map(id => [id, old[id]])) : old;
        });
    }, [signature]);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        let frame = 0;
        let disposed = false;
        const read = () => {
            frame = 0;
            if (disposed)
                return;
            const width = Math.round(el.clientWidth), height = Math.round(el.clientHeight);
            // Hidden/minimized zero sizes must not overwrite the restored geometry.
            if (!width || !height)
                return;
            setSize(old => old.width === width && old.height === height ? old : { width, height });
            // Text enlargement is allowed, not capped. Reserve its real height
            // below the image rather than drawing a label across a face.
            const labelSizes = Array.from(el.querySelectorAll(".voice-call-video-label"), label => label.offsetHeight);
            setLabelHeight(Math.max(32, ...labelSizes));
        };
        const schedule = () => {
            if (!disposed && !frame)
                frame = window.requestAnimationFrame(read);
        };
        read();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
        observer?.observe(el);
        el.querySelectorAll(".voice-call-video-label").forEach(label => observer?.observe(label));
        window.addEventListener("resize", schedule);
        window.addEventListener("orientationchange", schedule);
        return () => {
            disposed = true;
            observer?.disconnect();
            if (frame)
                window.cancelAnimationFrame(frame);
            window.removeEventListener("resize", schedule);
            window.removeEventListener("orientationchange", schedule);
        };
    }, [signature]);
    const activeFocus = focused && orderedIds.includes(focused) ? focused : null;
    const ready = size.width > PADDING * 2 && size.height > PADDING * 2 && ids.length > 0;
    const boxes = useMemo(() => {
        if (!ready)
            return new Map();
        const layout = layoutCameraGrid(size.width - PADDING * 2, size.height - PADDING * 2, orderedIds.map(id => ratios[id] ?? CAMERA_FALLBACK_RATIO), activeFocus ? orderedIds.indexOf(activeFocus) : -1, 54 + labelHeight);
        return new Map(orderedIds.map((id, index) => {
            const box = layout.boxes[index];
            return [id, {
                    position: "absolute", left: box.x + PADDING, top: box.y + PADDING,
                    width: box.width, height: box.height,
                }];
        }));
    }, [ready, size.width, size.height, orderedIds, ratios, activeFocus, labelHeight]);
    const value = useMemo(() => ({ boxes, setRatio, toggle, focused: activeFocus, count: orderedIds.length }), [boxes, setRatio, toggle, activeFocus, orderedIds.length]);
    return _jsx(CameraGridContext.Provider, { value: value, children: _jsx("div", { ref: ref, className: "voice-call-video-grid adaptive-camera-grid", style: { "--camera-label-height": `${labelHeight}px` }, "data-count": Math.max(ids.length, 1), "data-adaptive-ready": ready ? "true" : "false", onKeyDown: event => {
                if (event.key === "Escape" && activeFocus) {
                    event.stopPropagation();
                    setFocused(null);
                }
            }, children: children }) });
}
/** Real intrinsic dimensions, not track constraints or the CSS tile ratio.
 * Metadata/resize events are dimension changes, not per-frame work. */
export function useAdaptiveCameraTile(id, videoRef, stream, active) {
    const grid = useContext(CameraGridContext);
    const setRatio = grid?.setRatio;
    useEffect(() => {
        const video = videoRef.current;
        if (!id || !setRatio || !video)
            return;
        setRatio(id, CAMERA_FALLBACK_RATIO);
        if (!active)
            return;
        const read = () => {
            if (video.srcObject === stream && video.videoWidth > 0 && video.videoHeight > 0) {
                setRatio(id, video.videoWidth / video.videoHeight);
            }
        };
        video.addEventListener("loadedmetadata", read);
        video.addEventListener("resize", read);
        // A track can already be loaded when a hidden call is restored.
        if (video.readyState >= 1)
            read();
        return () => {
            video.removeEventListener("loadedmetadata", read);
            video.removeEventListener("resize", read);
        };
    }, [id, setRatio, videoRef, stream, active]);
    return grid && id ? {
        style: grid.boxes.get(id),
        enlarged: grid.focused === id,
        toggle: grid.count > 1 ? () => grid.toggle(id) : undefined,
    } : null;
}
